import { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { PageContainer, PageHeader } from "../components/PageHeader";
import SignageScreenOverview from "../components/SignageScreenOverview";
import { foyerApi, type AssetSummary, type ScreenRecord } from "../http/foyer";
import { thumbnailFor } from "../utils/signageItems";
import { tokens } from "../theme";
import { useScope } from "../context/ScopeContext";
import type {
  SignageAssignment,
  SignageCampaign,
  SignagePlaylist,
} from "@shared/types/signage";

/**
 * Every screen at this location, and what each one is doing right now.
 *
 * ## What this page is for, and what it deliberately is not
 *
 * It answers "are my walls alright" at a glance, across the estate. It is NOT
 * where anything is configured: scheduling lives on Plan content, which
 * answers the different question of "what did I set up". A card here can start
 * an action, but the page itself is read.
 *
 * ## Why an overview and a scheduler are two pages
 *
 * The first attempt put this under the scheduling page's screen picker, which
 * made it a per-screen detail view - you had to already know which wall you
 * were worried about. The whole value of an overview is not knowing: you scan
 * twenty screens and the one that is dark finds YOU.
 *
 * ## Where it sits in the nav
 *
 * Under Overview, beside Fleet health and the other estate-wide views, for a
 * platform admin. A company or premise admin is permitted none of those, so
 * the group collapses to this single item and `renderGroup` renders it flat -
 * an accordion over one row is a chevron promising something behind it.
 */

/** One card's worth of state, fetched per screen. */
interface Row {
  screen: ScreenRecord;
  assignment: SignageAssignment | undefined;
}

export default function SignageOverviewPage() {
  /*
   * Scoped from the header bar, like every other fleet-wide page - the
   * Customer / Location selectors in the design ARE this scope.
   *
   * A location is required for now and said so rather than assumed: with no
   * premise picked the honest answer is "which site?", and quietly showing the
   * first one would be a page that lies about what it is describing.
   * Aggregating every location in scope is the obvious next step and is a
   * fan-out of this same fetch.
   */
  const { scope } = useScope();
  const premiseId = scope.premiseId;

  if (!premiseId) return <PickALocation />;
  return <PremiseOverview premiseId={premiseId} />;
}

function PickALocation() {
  return (
    <PageContainer>
      <PageHeader
        eyebrow="Signage"
        title="Overview"
        subtitle="Every screen at a location, and what each one is playing."
      />
      <Box
        sx={{
          border: `1px solid ${tokens.divider}`,
          borderRadius: "12px",
          p: 5,
          textAlign: "center",
          bgcolor: tokens.surface,
        }}
        data-testid="signage-overview-no-scope"
      >
        <Typography sx={{ fontWeight: 600, color: tokens.ink }}>
          Pick a location
        </Typography>
        <Typography sx={{ fontSize: 13, color: tokens.inkMuted, mt: 0.5 }}>
          Choose one in the bar above to see its screens.
        </Typography>
      </Box>
    </PageContainer>
  );
}

function PremiseOverview({ premiseId }: { premiseId: string }) {
  const [screens, setScreens] = useState<ScreenRecord[]>([]);
  const [assignments, setAssignments] = useState<SignageAssignment[]>([]);
  const [playlists, setPlaylists] = useState<SignagePlaylist[]>([]);
  const [campaigns, setCampaigns] = useState<SignageCampaign[]>([]);
  const [assets, setAssets] = useState<AssetSummary[]>([]);
  /**
   * The premise's own zone, for the campaign lanes.
   *
   * Campaign windows are authored in premise-local `HH:mm`, so they can only be
   * drawn in that zone - formatting them with the browser's offset silently
   * retimes the whole strip for a chain operator managing a site abroad. The
   * content strip already carries the zone on its timeline and says so at
   * length; the lanes need it fetched, and a wrong one here would be the same
   * bug in a new place.
   */
  const [timezone, setTimezone] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * ONE clock for the whole page.
   *
   * Every card's playhead, progress bar and campaign strip reads from it, so
   * the estate ticks in lockstep instead of each card starting its own
   * interval and drifting a second apart from its neighbour.
   */
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    /*
     * FIFTEEN seconds, not one.
     *
     * This clock re-renders every screen card on the page - strip, ruler,
     * campaign lanes, all of it. At one second that was sixty full re-renders
     * a minute per card to move a playhead that crosses a quarter of a pixel
     * in that time.
     *
     * The one thing that genuinely wants a per-second answer, the "00:21 /
     * 00:30" readout, owns its own clock inside `ScreenRail` - so the fast
     * tick costs one small subtree instead of the page.
     */
    const id = setInterval(() => setNow(new Date()), 15_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const pull = () =>
      Promise.all([
        foyerApi.listScreens(premiseId),
        foyerApi.listSignageAssignments(premiseId),
        foyerApi.listSignagePlaylists(premiseId),
        foyerApi.listSignageCampaigns(premiseId),
      ])
        .then(([s, a, p, c]) => {
          if (!alive) return;
          // Signage screens only. An in-room TV and a door sign have no loop
          // and no campaigns; listing them would pad the page with cards that
          // can never say anything.
          setScreens(s.filter((x) => x.role === "signage"));
          setAssignments(a);
          setPlaylists(p);
          setCampaigns(c);
        })
        .catch((e: Error) => alive && setError(e.message))
        .finally(() => alive && setLoading(false));

    void pull();
    // Liveness expires. A screen that stops reporting has to go OFFLINE on
    // this page without somebody reloading it, or the badge is only a
    // snapshot of when the page was opened - the exact lie it exists to stop.
    const id = setInterval(pull, 30_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [premiseId]);

  useEffect(() => {
    let alive = true;
    foyerApi
      .getPremise(premiseId)
      .then((p) => alive && setTimezone(p.address?.timezone ?? null))
      .catch(() => alive && setTimezone(null));
    return () => {
      alive = false;
    };
  }, [premiseId]);

  useEffect(() => {
    let alive = true;
    foyerApi
      .listAssets({ kind: "premise", premiseId })
      // A name is a nicety; failing to fetch one must not take the page down.
      .catch(() => [])
      .then((list) => alive && setAssets(list as AssetSummary[]));
    return () => {
      alive = false;
    };
  }, [premiseId]);

  const assignmentFor = useMemo(
    () => new Map(assignments.map((a) => [a.screenId, a])),
    [assignments]
  );

  /** URL -> the name somebody actually gave the file. */
  const assetName = useMemo(() => {
    const byUrl = new Map(assets.map((a) => [a.url, a.originalName]));
    return (url: string) => byUrl.get(url) || undefined;
  }, [assets]);

  /**
   * URL -> a picture of it.
   *
   * `thumbnailFor` gives an uploaded VIDEO its poster frame, which is the only
   * way a customer's own loop gets a face on the strip: it has no channel
   * poster, and its first item is an mp4 - a file the browser will not draw in
   * an `<img>`. Without this the block falls back to a blank tile, which is
   * honest but says nothing about what is on the wall.
   */
  const assetThumb = useMemo(() => {
    const byUrl = new Map(assets.map((a) => [a.url, a]));
    return (url: string) => thumbnailFor(byUrl.get(url)) ?? undefined;
  }, [assets]);

  /**
   * Flip a campaign's priority from its lane.
   *
   * Optimistic, because the star is a toggle and a toggle that waits for a
   * round trip feels broken. Rolled back on failure rather than left showing
   * a state the server rejected - a lie that would survive until a reload.
   */
  const setPriority = async (
    campaign: SignageCampaign,
    priority: "normal" | "high"
  ) => {
    const before = campaigns;
    setCampaigns((list) =>
      list.map((c) => (c.id === campaign.id ? { ...c, priority } : c))
    );
    try {
      await foyerApi.updateSignageCampaign(premiseId, campaign.id, {
        priority,
      });
    } catch (e) {
      setCampaigns(before);
      setError(e instanceof Error ? e.message : "Could not save");
    }
  };

  const rows: Row[] = useMemo(
    () =>
      screens.map((screen) => ({
        screen,
        assignment: assignmentFor.get(screen.id),
      })),
    [screens, assignmentFor]
  );

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Signage"
        title="Overview"
        subtitle="Every screen at this location, and what each one is playing."
      />

      {error && (
        <Typography sx={{ color: tokens.error, mb: 2 }}>{error}</Typography>
      )}

      {loading || !timezone ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : rows.length === 0 ? (
        <Box
          sx={{
            border: `1px solid ${tokens.divider}`,
            borderRadius: "12px",
            p: 5,
            textAlign: "center",
            bgcolor: tokens.surface,
          }}
          data-testid="signage-overview-empty"
        >
          <Typography sx={{ fontWeight: 600, color: tokens.ink }}>
            No signage screens here yet
          </Typography>
          {/* The likeliest reason, and the one an operator can act on. The
              same sentence the scheduling page's picker uses, deliberately. */}
          <Typography sx={{ fontSize: 13, color: tokens.inkMuted, mt: 0.5 }}>
            Open a screen under Places &amp; Screens and set its role to
            Signage.
          </Typography>
        </Box>
      ) : (
        <Stack gap={3}>
          {rows.map(({ screen, assignment }) => (
            <SignageScreenOverview
              key={screen.id}
              screenId={screen.id}
              screenLabel={
                screen.roomId
                  ? `${screen.roomId}${screen.model ? ` · ${screen.model}` : ""}`
                  : (screen.model ?? screen.id)
              }
              premiseId={premiseId}
              timezone={timezone}
              lastSeenAt={screen.lastSeenAt}
              report={screen.live?.signage}
              assignment={assignment}
              playlists={playlists}
              now={now}
              assetName={assetName}
              assetThumb={assetThumb}
              /*
               * The lanes live INSIDE the strip, on its ruler.
               * As a second card below they read as an unrelated picture of a
               * different thing; sharing the ruler, the scroll and the
               * playhead makes "which campaign is eligible while THAT playlist
               * is on" something you see by looking down a line.
               */
              campaigns={campaigns.filter((c) =>
                (assignment?.campaignIds ?? []).includes(c.id)
              )}
              onSetPriority={setPriority}
              onUnpair={async () => {
                await foyerApi.unpairScreen(premiseId, screen.id);
                // Drop it locally at once rather than waiting for the 30s
                // poll: a card for a screen that is gone is worse than a
                // moment's optimism.
                setScreens((list) => list.filter((s) => s.id !== screen.id));
              }}
            />
          ))}
        </Stack>
      )}
    </PageContainer>
  );
}
